import { z } from 'zod';
import { getSupabaseClient } from './supabase-client';

export interface WholesalePriceRepository {
  listPrices(): Promise<Record<string, number>>;
  getByProductId(productId: string): Promise<number | null>;
  setPrice(productId: string, price: number): Promise<void>;
  removePrice(productId: string): Promise<void>;
}

const rowSchema = z.object({
  product_id: z.string(),
  price: z.union([z.number(), z.string()]),
});

export const supabaseWholesalePriceRepository: WholesalePriceRepository = {
  async listPrices() {
    const supabase = await getSupabaseClient();
    if (!supabase) {
      return {};
    }

    const { data, error } = await supabase
      .from('product_wholesale_prices')
      .select('product_id, price');

    if (error || !data) {
      return {};
    }

    return data.reduce<Record<string, number>>((acc, row) => {
      const parsed = rowSchema.safeParse(row);
      if (parsed.success) {
        acc[parsed.data.product_id] = Number(parsed.data.price);
      }
      return acc;
    }, {});
  },

  async getByProductId(productId) {
    const supabase = await getSupabaseClient();
    if (!supabase) {
      return null;
    }

    const { data, error } = await supabase
      .from('product_wholesale_prices')
      .select('price')
      .eq('product_id', productId)
      .maybeSingle();

    if (error || !data) {
      return null;
    }
    return Number(data.price);
  },

  async setPrice(productId, price) {
    const supabase = await getSupabaseClient();
    if (!supabase) {
      return;
    }

    await supabase
      .from('product_wholesale_prices')
      .upsert({ product_id: productId, price, updated_at: new Date().toISOString() });
  },

  async removePrice(productId) {
    const supabase = await getSupabaseClient();
    if (!supabase) {
      return;
    }

    await supabase.from('product_wholesale_prices').delete().eq('product_id', productId);
  },
};
